/**
 * Action-only supplemental content kept outside the generic post shell.
 */
import React from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "ui/Carousel";
import EntityAvatar from "components/Entities/EntityAvatar";

export interface ActionLocationRecord {
  is_virtual?: boolean;
  location?: {
    event_url?: string;
    address?: string;
    place_name?: string;
    latitude?: number;
    longitude?: number;
  };
  note?: string;
}

interface ActionDetailPanelsProps {
  places?: ActionLocationRecord[];
  targetIds: number[];
  imageCdn: string;
}

function getFaviconForUrl(url: string, imageCdn: string): string {
  try {
    const urlObject = new URL(url);
    return `https://icons.duckduckgo.com/ip2/${urlObject.hostname}.ico`;
  } catch {
    return `${imageCdn}/icons/link_icon.svg`;
  }
}

function ActionLocationSummary({
  place,
  imageCdn,
}: {
  place: ActionLocationRecord;
  imageCdn: string;
}): React.ReactNode {
  if (place.is_virtual) {
    const eventUrl = place.location?.event_url;

    if (!eventUrl) {
      return "Virtual action (no link provided)";
    }

    return (
      <div className="flex items-center gap-1 w-full min-w-0">
        <div className="flex items-center w-full min-w-0 gap-1">
          <a
            href={eventUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="external-link inline-flex items-center flex-shrink-0"
            onClick={(event) => event.stopPropagation()}
            aria-label="Open action link"
          >
            <img
              className="size-4 rounded-sm mr-1"
              src={getFaviconForUrl(eventUrl, imageCdn)}
              alt="Website icon"
              onError={(event) => {
                const image = event.currentTarget;
                image.onerror = null;

                try {
                  const hostname = new URL(eventUrl).hostname;
                  const fallbackServices = [
                    `https://www.google.com/s2/favicons?domain_url=${hostname}`,
                    `https://favicon.yandex.net/favicon/${hostname}`,
                    `https://f1.allesedv.com/16/${hostname}`,
                    `${imageCdn}/icons/link_icon.svg`,
                  ];

                  let fallbackIndex = 0;
                  const applyNextFallback = () => {
                    if (fallbackIndex >= fallbackServices.length) {
                      return;
                    }

                    image.src = fallbackServices[fallbackIndex];
                    fallbackIndex += 1;
                    if (fallbackIndex < fallbackServices.length) {
                      image.onerror = applyNextFallback;
                    }
                  };

                  applyNextFallback();
                } catch {
                  image.src = `${imageCdn}/icons/link_icon.svg`;
                }
              }}
            />
          </a>
          <a
            href={eventUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="external-link line-clamp-1 break-all"
            onClick={(event) => event.stopPropagation()}
          >
            {eventUrl}
          </a>
        </div>
      </div>
    );
  }

  const address = place.location?.address || "In-person action (no address provided)";
  const placeName = place.location?.place_name;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1">
        {placeName ? <span className="font-medium">{placeName}</span> : <span>{address}</span>}
      </div>
      {placeName && (
        <div className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">{address}</div>
      )}
    </div>
  );
}

const ActionDetailPanels: React.FC<ActionDetailPanelsProps> = ({ places = [], targetIds, imageCdn }) => {
  if (places.length === 0 && targetIds.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-2 text-sm mb-1 border border-eoe-border dark:border-eoe-dark-border rounded-md p-2 w-full">
      {places.length > 0 && (
        <div className="flex items-center">
          {places.length === 1 ? (
            <ActionLocationSummary place={places[0]} imageCdn={imageCdn} />
          ) : (
            <Carousel className="w-full flex flex-row">
              <CarouselContent>
                {places.map((place, index) => (
                  <CarouselItem key={`place-${index}`}>
                    <ActionLocationSummary place={place} imageCdn={imageCdn} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="h-6 w-6 left-0" />
              <CarouselNext className="h-6 w-6 right-0" />
            </Carousel>
          )}
        </div>
      )}

      {targetIds.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-left text-xs font-semibold text-eoe-text-sub dark:text-eoe-dark-text-sub">
            Targets
          </div>
          <Carousel className="w-full flex flex-row">
            <CarouselContent>
              {targetIds.map((targetId) => (
                <CarouselItem key={targetId} className="basis-auto pr-2">
                  <EntityAvatar
                    entityType="issue"
                    entityId={targetId}
                    showCloseChip={false}
                    showLink
                    className="min-w-[180px] max-w-[260px] sm:max-w-[320px] pr-2"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-between">
              <CarouselPrevious className="flex" size="circle" />
              <CarouselNext className="flex" size="circle" />
            </div>
          </Carousel>
        </div>
      )}
    </div>
  );
};

export default ActionDetailPanels;
